import type {
  QuaggaJSCodeReader,
  QuaggaJSConfigObject,
  QuaggaJSResultObject,
} from '@ericblade/quagga2';
import { validateBarcode } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';

export type ScannerModeId = 'store-labels' | 'retail-products' | 'compact-codes';

export interface ScannerMode {
  readonly id: ScannerModeId;
  readonly labelKey: TranslationKey;
  readonly descriptionKey: TranslationKey;
  readonly readers: readonly QuaggaJSCodeReader[];
  readonly allowedFormats: ReadonlySet<string>;
}

export interface ScanSample {
  readonly raw: string;
  readonly normalized: string;
  readonly format: string;
  readonly seenAt: number;
}

export interface ScanAnalysis {
  readonly sample: ScanSample | null;
  readonly rejectionReason: string | null;
}

export const SCANNER_MODE_STORAGE_KEY = 'dukanti.admin.barcodeScannerMode';
export const SAMPLE_WINDOW_MS = 8_000;
export const SAMPLE_SIZE = 5;

const CHECK_DIGIT_FORMATS = new Set(['ean_13', 'ean_8', 'upc_a', 'upc_e']);
const SHORT_FORMATS = new Set(['ean_8', 'upc_e']);
const DOMINANT_MATCHES = 4;
const SHORT_FORMAT_MATCHES = 5;

export const SCANNER_MODES: readonly ScannerMode[] = [
  {
    id: 'store-labels',
    labelKey: 'admin.barcode.scan.modeStoreLabels',
    descriptionKey: 'admin.barcode.scan.modeStoreLabelsDescription',
    readers: ['code_128_reader'],
    allowedFormats: new Set(['code_128']),
  },
  {
    id: 'retail-products',
    labelKey: 'admin.barcode.scan.modeRetailProducts',
    descriptionKey: 'admin.barcode.scan.modeRetailProductsDescription',
    readers: ['ean_reader', 'upc_reader'],
    allowedFormats: new Set(['ean_13', 'upc_a']),
  },
  {
    id: 'compact-codes',
    labelKey: 'admin.barcode.scan.modeCompactCodes',
    descriptionKey: 'admin.barcode.scan.modeCompactCodesDescription',
    readers: ['ean_8_reader', 'upc_e_reader'],
    allowedFormats: new Set(['ean_8', 'upc_e']),
  },
];

export function getScannerMode(modeId: ScannerModeId): ScannerMode {
  return SCANNER_MODES.find((mode) => mode.id === modeId) ?? SCANNER_MODES[0]!;
}

export function isScannerModeId(value: string | null): value is ScannerModeId {
  return SCANNER_MODES.some((mode) => mode.id === value);
}

export function getRequiredMatches(format: string): number {
  return SHORT_FORMATS.has(format) ? SHORT_FORMAT_MATCHES : DOMINANT_MATCHES;
}

export function buildQuaggaConfig(target: Element, mode: ScannerMode): QuaggaJSConfigObject {
  return {
    inputStream: {
      type: 'LiveStream',
      target,
      constraints: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      area: {
        top: '25%',
        right: '5%',
        bottom: '25%',
        left: '5%',
      },
      willReadFrequently: true,
    },
    locate: false,
    numOfWorkers: 0,
    frequency: 15,
    canvas: { createOverlay: false },
    locator: {
      halfSample: false,
      patchSize: 'small',
      willReadFrequently: true,
    },
    decoder: {
      readers: [...mode.readers],
      multiple: false,
    },
  };
}

export function getDominantSample(samples: readonly ScanSample[]): ScanSample | null {
  const latestSamples = samples.slice(-SAMPLE_SIZE);
  if (latestSamples.length < SAMPLE_SIZE) return null;

  const winner = getWinningSample(latestSamples);
  if (!winner) return null;

  return winner.count >= getRequiredMatches(winner.sample.format) ? winner.sample : null;
}

export function getBestSampleProgress(samples: readonly ScanSample[]): {
  readonly count: number;
  readonly required: number;
} | null {
  const latestSamples = samples.slice(-SAMPLE_SIZE);
  if (latestSamples.length === 0) return null;

  const winner = getWinningSample(latestSamples);
  if (!winner) return null;

  return {
    count: winner.count,
    required: getRequiredMatches(winner.sample.format),
  };
}

export function addSample(
  samples: readonly ScanSample[],
  nextSample: ScanSample,
  now: number,
): readonly ScanSample[] {
  return [...samples, nextSample]
    .filter((sample) => now - sample.seenAt <= SAMPLE_WINDOW_MS)
    .slice(-SAMPLE_SIZE);
}

export function analyzeQuaggaResult(
  result: QuaggaJSResultObject,
  now: number,
  allowedFormats: ReadonlySet<string>,
): ScanAnalysis {
  const raw = result.codeResult?.code;
  const format = getQuaggaFormat(result);
  if (!raw) {
    return {
      sample: null,
      rejectionReason: 'empty-code',
    };
  }

  const validation = validateBarcode(raw);
  if (!validation.valid || !validation.normalized) {
    return {
      sample: null,
      rejectionReason: validation.errorKey ?? 'barcode-validation-failed',
    };
  }

  if (!allowedFormats.has(format)) {
    return {
      sample: null,
      rejectionReason: 'unsupported-format',
    };
  }

  if (!hasValidFormatSpecificCheckDigit(format, validation.normalized)) {
    return {
      sample: null,
      rejectionReason: 'invalid-check-digit',
    };
  }

  return {
    sample: {
      raw,
      normalized: validation.normalized,
      format,
      seenAt: now,
    },
    rejectionReason: null,
  };
}

function getQuaggaFormat(result: QuaggaJSResultObject): string {
  const format = result.codeResult?.format ?? 'unknown';
  return format === 'upc' ? 'upc_a' : format;
}

function sampleKey(sample: Pick<ScanSample, 'normalized' | 'format'>): string {
  return `${sample.format}:${sample.normalized}`;
}

function getWinningSample(
  samples: readonly ScanSample[],
): { sample: ScanSample; count: number } | null {
  const counts = new Map<string, { sample: ScanSample; count: number }>();
  samples.forEach((sample) => {
    const key = sampleKey(sample);
    const previous = counts.get(key);
    counts.set(key, { sample, count: (previous?.count ?? 0) + 1 });
  });

  return Array.from(counts.values()).sort((a, b) => b.count - a.count)[0] ?? null;
}

function hasValidGtinCheckDigit(value: string): boolean {
  if (!/^\d+$/.test(value) || value.length < 2) return false;

  const checkDigit = Number(value.at(-1));
  const body = value.slice(0, -1);
  let sum = 0;

  for (
    let index = body.length - 1, weight = 3;
    index >= 0;
    index -= 1, weight = weight === 3 ? 1 : 3
  ) {
    sum += Number(body[index]) * weight;
  }

  return (10 - (sum % 10)) % 10 === checkDigit;
}

function expandUpcE(value: string): string | null {
  if (!/^\d{8}$/.test(value)) return null;

  const numberSystem = value[0];
  if (numberSystem !== '0' && numberSystem !== '1') return null;

  const manufacturer = value.slice(1, 6);
  const expansionCode = value[6];
  const checkDigit = value[7];

  if (expansionCode === '0' || expansionCode === '1' || expansionCode === '2') {
    return `${numberSystem}${manufacturer.slice(0, 2)}${expansionCode}0000${manufacturer.slice(2)}${checkDigit}`;
  }

  if (expansionCode === '3') {
    return `${numberSystem}${manufacturer.slice(0, 3)}00000${manufacturer.slice(3)}${checkDigit}`;
  }

  if (expansionCode === '4') {
    return `${numberSystem}${manufacturer.slice(0, 4)}00000${manufacturer.slice(4)}${checkDigit}`;
  }

  return `${numberSystem}${manufacturer}0000${expansionCode}${checkDigit}`;
}

function hasValidFormatSpecificCheckDigit(format: string, normalized: string): boolean {
  if (!CHECK_DIGIT_FORMATS.has(format)) return true;
  if (format === 'ean_13') return /^\d{13}$/.test(normalized) && hasValidGtinCheckDigit(normalized);
  if (format === 'ean_8') return /^\d{8}$/.test(normalized) && hasValidGtinCheckDigit(normalized);
  if (format === 'upc_a') return /^\d{12}$/.test(normalized) && hasValidGtinCheckDigit(normalized);

  const expanded = expandUpcE(normalized);
  return expanded !== null && hasValidGtinCheckDigit(expanded);
}
